import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

import type { GuideCategory } from '@/lib/data/guides/types';

const CATEGORY_GRADIENTS: Record<GuideCategory, string> = {
  mauritius: 'from-teal-900 via-cyan-800 to-stone-900',
  provence: 'from-amber-800 via-stone-700 to-purple-900',
  investment: 'from-stone-900 via-stone-800 to-amber-900/30',
  lifestyle: 'from-emerald-900 via-stone-800 to-stone-900',
};

export interface StoneGuideHeroProps {
  title: string;
  categoryLabel: string;
  category?: GuideCategory;
  heroImage?: string;
  locale: string;
  backLabel?: string;
}

export function StoneGuideHero({
  title,
  categoryLabel,
  category = 'mauritius',
  locale,
  backLabel,
}: StoneGuideHeroProps) {
  const label = backLabel ?? (locale === 'fr' ? 'Tous les guides' : 'All guides');
  const gradient = CATEGORY_GRADIENTS[category] || CATEGORY_GRADIENTS.mauritius;

  return (
    <section className="relative isolate h-[60vh] min-h-[420px] max-h-[700px] overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />

      <div className="absolute inset-0 flex items-end">
        <div className="container mx-auto max-w-7xl px-6 md:px-12 pb-12 md:pb-16">
          <Link
            href={`/${locale}/guides`}
            className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-white transition-colors mb-8 font-sans"
          >
            <ArrowLeft size={16} strokeWidth={1} aria-hidden="true" />
            {label}
          </Link>

          <span className="inline-block text-[10px] uppercase tracking-[0.3em] text-stone-300 font-sans border border-stone-500/50 px-4 py-2 mb-4">
            {categoryLabel}
          </span>

          <h1
            className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-[1.05] italic max-w-4xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h1>
        </div>
      </div>
    </section>
  );
}

export default StoneGuideHero;
